if (!customElements.get('sw-collection-roll')) {
  customElements.define('sw-collection-roll', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('.sw-roll__track');
      this.prev = this.querySelector('[data-sw-prev]');
      this.next = this.querySelector('[data-sw-next]');
      this.count = this.querySelector('[data-sw-count]');
      this.status = this.querySelector('[data-sw-status]');
      this.allLink = this.querySelector('[data-sw-all]');
      this.defaultAllUrl = this.allLink?.getAttribute('href');
      this.audiences = Array.from(this.querySelectorAll('[data-sw-audience]'));
      this.tabs = Array.from(this.querySelectorAll('[data-sw-tab]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-sw-filter]'));
      this.colorButtons = Array.from(this.querySelectorAll('[data-sw-color]'));
      this.filterButtons.push(...this.colorButtons);
      this.clearFilter = this.querySelector('[data-sw-clear]');
      this.filterLabel = this.querySelector('[data-sw-filter-label]');
      this.activeFilters = [];
      this.pendingFilters = null;
      this.filterResults = new Map();
      this.availability = new Map();
      this.baseUrl = this.dataset.url;
      this.cache = new Map();
      const batch = this.querySelector('[data-sw-batch]');
      this.state = {url:this.dataset.url, page:batch?.dataset.next, total:Number(batch?.dataset.total || 0), items:this.items};
      this.cache.set(this.state.url, this.state);
      this.index = 0;
      this.sequence = 0;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.prev.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.audiences.forEach(button => button.addEventListener('click', () => this.selectAudience(button), options));
      this.tabs.forEach(tab => tab.addEventListener('click', () => this.select(tab), options));
      this.filterButtons.forEach(button => button.addEventListener('click', () => this.filterBy(button), options));
      this.clearFilter?.addEventListener('click', () => this.filterBy(null), options);
      this.track.addEventListener('click', event => this.remember(event), {...options, capture:true});
      this.restoreCancelled = false;
      const cancelRestore = () => { this.restoreCancelled = true; };
      window.addEventListener('pointerdown', cancelRestore, {...options, once:true, passive:true});
      window.addEventListener('keydown', cancelRestore, {...options, once:true});
      this.addEventListener('shopify:block:select', event => {
        const tab = event.target.closest('[data-sw-tab]') || event.target.querySelector('[data-sw-tab]');
        if (tab && this.contains(tab)) this.select(tab);
      }, options);
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(true), 100);
      }, {...options, passive:true});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.resize = new ResizeObserver(() => this.update(false));
      this.resize.observe(this.track);
      this.classList.add('is-ready');
      this.update(false);
      if (history.state?.swCarouselReturn?.section === this.dataset.sectionId) this.restorePosition();
      else this.select(this.tabs.find(tab => tab.dataset.url === this.baseUrl));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      this.sequence++;
      this.resize?.disconnect();
      clearTimeout(this.timer);
      this.ready = false;
    }
    remember(event) {
      const link = event.target.closest('a[href]');
      const item = link?.closest('.sw-roll__item');
      if (!item || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      try {
        const saved = {url:this.state.url, productId:item.dataset.productId, index:this.items.indexOf(item), scrollY:window.scrollY};
        history.replaceState({...history.state, swCarouselReturn:{section:this.dataset.sectionId, ...saved}}, '');
      } catch (_) {}
    }
    async restorePosition() {
      const saved = history.state?.swCarouselReturn;
      if (!saved || saved.section !== this.dataset.sectionId || !Number.isInteger(saved.index) || saved.index < 0) return;
      const savedUrl = new URL(saved.url, location.origin);
      if (savedUrl.origin !== location.origin) return;
      const tab = this.tabs.find(button => button.dataset.url === savedUrl.pathname);
      const filters = this.filterButtons.filter(button => (!button.dataset.collection || button.dataset.collection === savedUrl.pathname) && this.values(button).every(value => savedUrl.searchParams.getAll(button.dataset.param).includes(value)));
      if (savedUrl.pathname !== this.dataset.url && !tab) return;
      try {
        if (saved.url !== this.state.url) await this.select(tab, filters);
        const sequence = this.sequence;
        const valid = () => this.isConnected && !this.restoreCancelled && sequence === this.sequence && saved.url === this.state.url;
        if (!valid()) return;
        while (!this.items.some(item => item.dataset.productId === saved.productId) && this.state.page && this.items.length <= saved.index) {
          const length = this.items.length;
          await this.load();
          if (!valid() || this.failed || this.items.length === length) return;
        }
        if (document.readyState !== 'complete') await new Promise(resolve => window.addEventListener('load', resolve, {once:true, signal:this.controller.signal}));
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (!valid()) return;
        const item = this.items.find(item => item.dataset.productId === saved.productId) || this.items[Math.min(saved.index, this.items.length - 1)];
        if (!item) return;
        const rect = item.getBoundingClientRect();
        const trackRect = this.track.getBoundingClientRect();
        const offset = this.columns === 1 ? (this.track.clientWidth - rect.width) / 2 : 0;
        this.track.scrollTo({left:this.track.scrollLeft + rect.left - trackRect.left - offset, behavior:'instant'});
        this.update(false);
        if (Number.isFinite(saved.scrollY)) window.scrollTo({top:saved.scrollY, behavior:'instant'});
      } catch (_) {}
    }
    get items() { return Array.from(this.track.querySelectorAll('.sw-roll__item')); }
    get columns() { return innerWidth < 750 ? 1 : innerWidth < 1150 ? 3 : 4; }
    values(button) { return button.dataset.values ? JSON.parse(button.dataset.values) : [button.dataset.value]; }
    async availableFilters(baseUrl) {
      if (!this.availability.has(baseUrl)) {
        const pending = this.fetchBatch(baseUrl, 1, this.controller.signal).then(batch => {
          if (!batch.available) throw new Error('filters');
          return batch.available;
        }).catch(() => {
          this.availability.delete(baseUrl);
          return null;
        });
        this.availability.set(baseUrl, pending);
      }
      return this.availability.get(baseUrl);
    }
    async fetchBatch(collectionUrl, page, signal) {
      const url = new URL(collectionUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('origin');
      const conditions = [...url.searchParams].filter(([key]) => key.startsWith('filter.'));
      const groups = conditions.filter(([key]) => key !== 'filter.p.m.custom.color').map(condition => [condition]);
      const colors = conditions.filter(([key]) => key === 'filter.p.m.custom.color');
      if (colors.length) groups.push(colors);
      if (groups.length > 1) {
        const results = await Promise.all(groups.map(async group => {
          const single = new URL(url.pathname, location.origin);
          group.forEach(([key, value]) => single.searchParams.append(key, value));
          const path = single.pathname + single.search;
          if (this.filterResults.has(path)) return this.filterResults.get(path);
          const items = [];
          const pages = new Set();
          let next = 1;
          while (next) {
            if (pages.has(String(next))) throw new Error('pagination');
            pages.add(String(next));
            const batch = await this.fetchBatch(path, next, signal);
            items.push(...batch.items);
            next = batch.page;
          }
          this.filterResults.set(path, items);
          return items;
        }));
        const sets = results.slice(1).map(items => new Set(items.map(item => item.dataset.productId)));
        const items = results[0].filter(item => sets.every(ids => ids.has(item.dataset.productId))).map(item => item.cloneNode(true));
        return {url:collectionUrl, page:'', total:items.length, items};
      }
      url.searchParams.set('section_id', 'sw-carousel-page');
      url.searchParams.set('page', page);
      const response = await fetch(url, {signal});
      if (!response.ok) throw new Error('fetch');
      const html = (await response.text()).replaceAll('sw-carousel-page', this.dataset.sectionId);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const batch = doc.querySelector('[data-sw-batch]');
      if (!batch) throw new Error('batch');
      const metadata = doc.querySelector('[data-sw-available-filters]');
      return {url:collectionUrl, page:batch.dataset.next, total:Number(batch.dataset.total || 0), items:Array.from(batch.querySelectorAll('.sw-roll__item')), available:metadata ? JSON.parse(metadata.textContent) : null};
    }
    syncAudience(tab) {
      if (!this.audiences.length || !tab) return;
      this.audiences.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.swAudience === tab.dataset.audience)));
      this.tabs.forEach(button => { button.hidden = button.dataset.audience !== tab.dataset.audience; });
      if (this.allLink) this.allLink.setAttribute('href', tab.dataset.allUrl || this.defaultAllUrl);
    }
    selectAudience(button) {
      const current = this.tabs.find(tab => tab.dataset.url === this.baseUrl);
      const candidates = this.tabs.filter(tab => tab.dataset.audience === button.dataset.swAudience);
      const target = candidates.find(tab => tab.dataset.kind === current?.dataset.kind) || candidates[0];
      if (!target) return;
      const filters = (this.pendingFilters || this.activeFilters).map(filter => {
        if (!filter.hasAttribute('data-sw-color')) return filter;
        return this.colorButtons.find(color => color.dataset.collection === target.dataset.url && color.getAttribute('aria-label') === filter.getAttribute('aria-label'));
      }).filter(Boolean);
      return this.select(target, filters);
    }
    filterBy(filter) {
      const tab = this.tabs.find(button => button.dataset.url === this.baseUrl);
      const selected = new Set(this.pendingFilters || this.activeFilters);
      if (!filter) selected.clear();
      else if (selected.has(filter)) selected.delete(filter);
      else {
        if (filter.hasAttribute('data-sw-color')) this.colorButtons.forEach(button => selected.delete(button));
        selected.add(filter);
      }
      return this.select(tab, this.filterButtons.filter(button => selected.has(button)));
    }
    async select(tab, filters = this.pendingFilters || this.activeFilters) {
      const sequence = ++this.sequence;
      this.request?.abort();
      this.pendingFilters = filters;
      const baseUrl = tab?.dataset.url || this.dataset.url;
      const hiVis = this.colorButtons.some(button => button.dataset.collection === baseUrl);
      const reflective = button => button.dataset.value === 'gid://shopify/FilterSettingGroup/250151252';
      const available = await this.availableFilters(baseUrl);
      if (sequence !== this.sequence || !this.isConnected) return;
      const unavailable = button => button.hasAttribute('data-sw-filter') && available && !available.some(group => group.param === button.dataset.param && group.values.some(value => String(value.value) === button.dataset.value && value.count > 0));
      filters = filters.filter(filter => (!filter.dataset.collection || filter.dataset.collection === baseUrl) && !(hiVis && reflective(filter)) && !unavailable(filter));
      const showFilters = () => this.filterButtons.filter(button => button.hasAttribute('data-sw-filter')).forEach(button => {
        const hidden = (hiVis && reflective(button)) || Boolean(unavailable(button));
        button.hidden = hidden;
        button.style.display = hidden ? 'none' : '';
      });
      const target = new URL(baseUrl, location.origin);
      filters.forEach(filter => this.values(filter).forEach(value => target.searchParams.append(filter.dataset.param, value)));
      const url = target.pathname + target.search;
      if (url === this.state.url && !this.switching) { showFilters(); this.syncAudience(tab); this.pendingFilters = null; return; }
      this.pendingFilters = filters;
      this.request?.abort();
      this.request = new AbortController();
      this.loading = null;
      this.switching = true;
      this.failed = false;
      [...this.tabs, ...this.filterButtons].forEach(button => button.removeAttribute('aria-busy'));
      const trigger = filters[filters.length - 1] || tab || this.clearFilter;
      trigger?.setAttribute('aria-busy', 'true');
      this.classList.add('is-switching');
      this.track.setAttribute('aria-busy', 'true');
      this.prev.disabled = this.next.disabled = true;
      this.status.textContent = this.dataset.loading;
      try {
        const state = this.cache.get(url) || await this.fetchBatch(url, 1, this.request.signal);
        if (sequence !== this.sequence || !this.isConnected) return;
        this.cache.set(url, state);
        this.state = state;
        this.baseUrl = baseUrl;
        this.syncAudience(tab);
        this.querySelectorAll('[data-sw-colors]').forEach(group => { group.hidden = group.dataset.collection !== baseUrl; });
        this.activeFilters = filters;
        showFilters();
        this.filterButtons.forEach(button => button.setAttribute('aria-pressed', String(filters.includes(button))));
        this.clearFilter?.setAttribute('aria-pressed', String(!filters.length));
        if (this.filterLabel) this.filterLabel.textContent = filters.map(button => button.getAttribute('aria-label')).join(' + ') || this.filterLabel.dataset.hint;
        this.track.replaceChildren(...state.items);
        this.track.scrollTo({left:0, behavior:'instant'});
        this.index = 0;
        this.tabs.forEach(button => button.setAttribute('aria-pressed', String(button === tab)));
        this.status.textContent = state.total ? '' : this.dataset.empty;
      } catch (error) {
        if (sequence === this.sequence && error.name !== 'AbortError') this.status.textContent = this.dataset.error;
      } finally {
        if (sequence === this.sequence) {
          this.switching = false;
          this.pendingFilters = null;
          this.classList.remove('is-switching');
          this.track.removeAttribute('aria-busy');
          trigger?.removeAttribute('aria-busy');
          this.update(false);
        }
      }
    }
    update(load) {
      if (this.switching) return;
      const items = this.items;
      const total = this.state.total;
      if (!items.length) {
        this.prev.disabled = this.next.disabled = true;
        this.count.textContent = '0 / 0';
        return;
      }
      const rect = this.track.getBoundingClientRect();
      const target = this.columns === 1 ? rect.left + rect.width / 2 : rect.left;
      let closest = Infinity;
      items.forEach((item, i) => {
        const box = item.getBoundingClientRect();
        const distance = Math.abs((this.columns === 1 ? box.left + box.width / 2 : box.left) - target);
        if (distance < closest) { closest = distance; this.index = i; }
      });
      items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
      this.count.textContent = `${this.index + 1}${this.columns > 1 ? '–' + Math.min(this.index + this.columns, total) : ''} / ${total}`;
      this.prev.disabled = this.index === 0;
      this.next.disabled = this.index + this.columns >= total || !!this.loading;
      if (load && !this.failed && this.state.page && this.index + this.columns >= items.length - 4) this.load();
    }
    async load() {
      if (!this.state.page || this.switching) return;
      if (this.loading) return this.loading;
      const state = this.state;
      const sequence = this.sequence;
      this.request = new AbortController();
      this.failed = false;
      this.status.textContent = this.dataset.loading;
      this.next.disabled = true;
      this.track.setAttribute('aria-busy', 'true');
      this.loading = (async () => {
        try {
          const batch = await this.fetchBatch(state.url, state.page, this.request.signal);
          if (sequence !== this.sequence || !this.isConnected) return;
          const existing = new Set(state.items.map(item => item.dataset.productId));
          const fresh = batch.items.filter(item => !existing.has(item.dataset.productId));
          if (!fresh.length) throw new Error('empty');
          state.items.push(...fresh);
          state.page = batch.page;
          state.total = batch.total;
          this.track.append(...fresh);
          this.status.textContent = '';
        } catch (error) {
          if (sequence === this.sequence && error.name !== 'AbortError') {
            this.failed = true;
            this.status.textContent = this.dataset.error;
          }
        } finally {
          if (sequence === this.sequence) {
            this.loading = null;
            this.track.removeAttribute('aria-busy');
            if (this.isConnected) this.update(false);
          }
        }
      })();
      return this.loading;
    }
    async move(direction) {
      if (this.switching) return;
      const sequence = this.sequence;
      const targetIndex = Math.max(0, Math.min(this.state.total - this.columns, this.index + direction * this.columns));
      if (direction > 0 && targetIndex + this.columns > this.items.length && this.state.page) await this.load();
      if (sequence !== this.sequence) return;
      const item = this.items[targetIndex];
      if (!item) return;
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const offset = this.columns === 1 ? (this.track.clientWidth - itemRect.width) / 2 : 0;
      this.track.scrollTo({left:this.track.scrollLeft + itemRect.left - trackRect.left - offset, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
  });
}




